import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The allowed sites of one website widget (plan-004 WC-81, decision D2 (a), design §17.6.3).
 *
 * Add, remove, check as you type, then Save — one `PUT` of the whole set, because the set is what
 * the API keeps and what the audit entry records. Nothing is sent until Save; Discard puts the list
 * back as it is saved.
 *
 * AS YOU TYPE, the box says what will be saved ("Saved as https://roma.example") or the one thing to
 * change, from `originAdviceOf` — whose acceptance is `normaliseOrigin`, the API's own function. A
 * duplicate and a 21st site are refused here with the API's bound. An empty list while the chat or
 * its voice is on is refused here with the API's own sentence, so an owner does not switch the chat
 * off everywhere by removing its last site; anything the API still refuses is shown in its words.
 *
 * AN ORIGIN CHECK IS NOT AUTHENTICATION, and the editor says so: it stops the widget being put on
 * somebody else's site, because browsers send an honest `Origin`; it is not a password.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Field, Input } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiErrorText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import { useHelpFillable } from "../../help/fill/use-help-fillable.js";
import { QUERY_KEY } from "../../api/query.js";
import type { WebsiteWidgetView } from "../../api/slice/messaging.js";
import { ORIGIN_RULE, WEBSITE_WIDGET_ORIGIN_MAX, originAdviceOf, sameOriginSet } from "./origin-rule.js";

/** The API's sentence for an empty list while the chat or its voice is on (422 `origin_list_empty`). */
export const EMPTY_WHILE_ON_TEXT =
  () => (portalText("operations", "the_allowed_list_cannot_be_empty_while_the_chat_is_on_or_v_7c3f51d4"));

export interface OriginsEditorProps {
  readonly widget: WebsiteWidgetView;
  /** False for Members and Viewers: the list is shown, with `refusedBecause`, and cannot be changed. */
  readonly canEdit: boolean;
  readonly refusedBecause: string | null;
}

export function OriginsEditor({ widget, canEdit, refusedBecause }: OriginsEditorProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<readonly string[]>(widget.allowedOrigin);
  const [typed, setTyped] = useState("");
  const [shownProblem, setShownProblem] = usePortalMessage();
  const [saved, setSaved] = useState(false);
  // plan-019 WAPP-FORM: the help assistant may propose the typed box only, and only to a person who may edit; Add,
  // Remove and Save stay the person's own buttons (research form-fill §5c).
  const typeOrigin = (next: string): void => {
    setTyped(next);
    setShownProblem(null);
  };
  const helpFill = useHelpFillable("website_widget_origin", {
    typed_origin: canEdit ? { value: typed, set: typeOrigin } : undefined,
  });

  const save = useMutation({
    mutationFn: (next: readonly string[]) => gateway.replaceWebChatWidgetOrigins(widget.channelBindingId, next),
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.messagingMedium });
    },
  });

  const advice = originAdviceOf(typed);
  const typedProblem =
    advice.kind === "refused"
      ? advice.problem
      : advice.kind === "accepted" && draft.includes(advice.origin)
        ? portalText("operations", "value_is_already_on_the_list_8876290d", { v0: String(advice.origin) })
        : advice.kind === "accepted" && draft.length >= WEBSITE_WIDGET_ORIGIN_MAX
          ? portalText("operations", "a_widget_lists_at_most_value_sites_remove_one_first_37dee7b8", { v0: String(String(WEBSITE_WIDGET_ORIGIN_MAX)) })
          : null;
  const dirty = !sameOriginSet(draft, widget.allowedOrigin);
  const originCount = portalText("operations", "value_of_value_20e31d2d", { v0: String(String(draft.length)), v1: String(String(WEBSITE_WIDGET_ORIGIN_MAX)) });

  const add = (): void => {
    if (advice.kind !== "accepted" || typedProblem !== null) {
      setShownProblem(() => (typedProblem ?? portalText("operations", "type_the_site_s_address_first_such_as_https_www_example_co_806c6d44")));
      return;
    }
    setDraft([...draft, advice.origin].sort());
    setTyped("");
    setShownProblem(null);
    setSaved(false);
    save.reset();
  };

  const remove = (origin: string): void => {
    setDraft(draft.filter((one) => one !== origin));
    setShownProblem(null);
    setSaved(false);
    save.reset();
  };

  const submit = (): void => {
    if (draft.length === 0 && (widget.enabled || widget.voiceEnabled)) {
      setShownProblem(() => (EMPTY_WHILE_ON_TEXT()));
      return;
    }
    setShownProblem(null);
    save.mutate(draft);
  };

  return (
    <div className="mg-origins">
      <p className="mg-section-title">{portalText("operations", "sites_it_answers_on_21830db1")}</p>
      <p className="mg-card-note">
        {portalText("operations", "the_chat_answers_only_on_the_sites_listed_here_on_any_othe_da05b033")}</p>

      {draft.length === 0 ? (
        <p className="mg-origin-empty">{portalText("operations", "no_site_is_listed_so_the_chat_answers_nowhere_80cf3f26")}</p>
      ) : (
        <ul className="mg-origin-set" aria-label={portalText("operations", "allowed_sites_for_value_c1de9138", { v0: String(widget.voiceAgentName) })}>
          {draft.map((origin) => (
            <li className="mg-origin-row" key={origin}>
              <span className="mg-origin-value">{origin}</span>
              {canEdit ? (
                <Button
                  variant="ghost"
                  aria-label={portalText("operations", "remove_value_f7ef029d", { v0: String(origin) })}
                  onClick={() => {
                    remove(origin);
                  }}
                >
                  {portalText("operations", "remove_e963907d")}</Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canEdit ? (
        <>
          <div className="mg-origin-add">
            <Field
              label={portalText("operations", "add_a_site_address_value_d0caf397", { v0: String(originCount) })}
              {...(typed.trim() !== "" && typedProblem !== null
                ? { error: typedProblem }
                : advice.kind === "accepted"
                  ? { description: portalText("operations", "saved_as_value_a62e2eea", { v0: String(advice.origin) }) }
                  : { description: portalText("operations", "for_example_https_www_example_com_56c2f217") })}
            >
              {(control) => (
                <Input
                  {...control}
                  font="mono"
                  inputMode="url"
                  autoComplete="url"
                  spellCheck={false}
                  value={typed}
                  placeholder="https://www.example.com"
                  {...helpFill.mark("typed_origin")}
                  onChange={(event) => {
                    typeOrigin(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      add();
                    }
                  }}
                />
              )}
            </Field>
            <Button variant="secondary" onClick={add}>
              {portalText("operations", "add_61cc55aa")}</Button>
          </div>

          <div className="mg-card-actions">
            <Button variant="primary" disabled={!dirty || save.isPending} onClick={submit}>
              {save.isPending ? portalText("operations", "saving_56a2285c") : portalText("operations", "save_sites_f15efaf6")}
            </Button>
            {dirty ? (
              <Button
                variant="ghost"
                disabled={save.isPending}
                onClick={() => {
                  setDraft(widget.allowedOrigin);
                  setShownProblem(null);
                  save.reset();
                }}
              >
                {portalText("operations", "discard_changes_fa8fc043")}</Button>
            ) : null}
            <span className="mg-card-note" role="status">
              {dirty && !save.isPending ? portalText("operations", "not_saved_yet_36b32f52") : saved && !dirty ? portalText("operations", "saved_9d4af690") : ""}
            </span>
          </div>
          {shownProblem !== null && typed.trim() === "" ? (
            <p className="c-note c-note-critical" role="alert">
              {shownProblem}
            </p>
          ) : null}
          {save.isError ? (
            <p className="c-note c-note-critical" role="alert">
              {apiErrorText(save.error, portalText("operations", "the_sites_could_not_be_saved_0706ea8c"))} {portalText("operations", "nothing_was_changed_c60d5d2a")}</p>
          ) : null}

          <details className="mg-rule">
            <summary>{portalText("operations", "how_addresses_are_checked_9723f138")}</summary>
            <ul className="mg-rule-set">
              {ORIGIN_RULE().map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
          </details>
        </>
      ) : (
        <p className="mg-card-note">{refusedBecause}</p>
      )}
    </div>
  );
}
